import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteUsers, type GetUsersData, getUser, getUsers, type UpdateUserData, type User, updateUser } from 'sdk';
import { appConfig } from 'shared';
import type { ApiError } from '~/lib/api';
import { useUpdateSelfMutation } from '~/modules/me/query';
import { usersSearchDefaults } from '~/modules/user/search-params-schemas';
import type { BaseUser } from '~/modules/user/types';
import { useUserStore } from '~/modules/user/user-store';
import { cacheRemove, cacheUpdate, removeDetailQueriesById } from '~/query/basic/cache-mutations';
import { createEntityKeys } from '~/query/basic/create-query-keys';
import { createCacheFinder } from '~/query/basic/find-in-list-cache';
import { offsetPaging, pageQuery } from '~/query/basic/infinite-query-options';
import { invalidateIfLastMutation } from '~/query/basic/invalidation-helpers';
import type { MutationData } from '~/query/types';

type UserFilters = Omit<NonNullable<GetUsersData['query']>, 'limit' | 'offset'>;
type UsersListParams = UserFilters & { limit?: number };

const keys = createEntityKeys<UserFilters>('user');

export const userQueryKeys = keys;

const findUserInCache = createCacheFinder<User>('user');

export const userQueryOptions = (id: string) =>
  queryOptions({
    queryKey: keys.detail.byId(id),
    // include=mfa: the profile header shows the MFA setting to whoever the API returns it to
    queryFn: () => getUser({ path: { relatableUserId: id }, query: { include: 'mfa' } }),
    placeholderData: () => findUserInCache(id),
  });

export const usersListQueryOptions = (params: UsersListParams) => {
  const defaults = usersSearchDefaults;
  const { q = defaults.q, sort = defaults.sort, order = defaults.order, role, limit = appConfig.requestLimits.users } = params;
  const filters = { q, sort, order, role };

  return infiniteQueryOptions({
    queryKey: keys.list.filtered(filters),
    ...offsetPaging(limit, (offset, signal) => getUsers({ query: { ...filters, ...pageQuery(limit, offset) }, signal })),
    refetchOnMount: true,
  });
};

const useUserUpdateMutation = () => {
  const queryClient = useQueryClient();
  const listKey = keys.list.base;

  return useMutation<User, ApiError, MutationData<UpdateUserData>>({
    mutationKey: keys.update,
    mutationFn: ({ path, body }) => updateUser({ path, body }),
    onSuccess: (updatedUser) => {
      cacheUpdate(listKey, [updatedUser]);
      queryClient.invalidateQueries({ queryKey: keys.detail.base });
    },
    onSettled: () => {
      invalidateIfLastMutation(queryClient, keys.all, listKey);
    },
    gcTime: 1000 * 10,
  });
};

/** Saves a user's profile: the signed-in user's through `/me`, which also updates the user store, anyone else's as an admin. */
export const useUpdateUser = (userId: string) => {
  const isSelf = useUserStore((state) => state.user?.id === userId);
  const selfMutation = useUpdateSelfMutation();
  const adminMutation = useUserUpdateMutation();

  const mutate = (body: UpdateUserData['body'], options?: { onSuccess?: (user: User) => void }) =>
    isSelf ? selfMutation.mutate(body, options) : adminMutation.mutate({ path: { id: userId }, body }, options);

  return { mutate, isPending: selfMutation.isPending || adminMutation.isPending };
};

export const useUserDeleteMutation = () => {
  const queryClient = useQueryClient();
  const listKey = keys.list.base;

  return useMutation<void, ApiError, BaseUser[]>({
    mutationKey: keys.delete,
    mutationFn: async (users) => {
      const ids = users.map(({ id }) => id);
      await deleteUsers({ body: { ids } });
    },
    onSuccess: (_, users) => {
      cacheRemove(listKey, users);
      removeDetailQueriesById(
        queryClient,
        keys.detail.base,
        users.map(({ id }) => id),
      );
    },
    onSettled: () => {
      invalidateIfLastMutation(queryClient, keys.all, listKey);
    },
  });
};
